import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import {
  WS_RECONNECTING_GRACE_MS,
  useConnectivityStore,
  wsReconnectingForMs,
  type ConnectivityState,
} from '@/lib/connectivity';
import {
  accountAttentionLabel,
  attentionReasonLabel,
  ESCALATE_TO_ADMIN_SUFFIX,
  isBanStormHeld,
} from '@/features/accounts/attention-labels';
import {
  formatAccountLabel,
  visibleAccounts,
  type Account,
  type AccountAttention,
} from '@/features/accounts/accounts.api';
import { useAccounts } from '@/features/accounts/useAccounts';
import { cn } from '@/lib/utils';

/**
 * App-shell top banner — the "prominent banner" half of the 2026-09 disconnect
 * early warning (spec `docs/specs/2026-09-features/01-disconnect-early-warning.md`
 * §3.5). Mounted in `Layout.tsx` ABOVE the resizable panel group so it stays
 * visible even with the account rail collapsed — the gap `01` §2 item 4 / E10
 * called out (the OLD `QrAttentionBanner` / `BanAttentionBanner` live INSIDE
 * `AccountSidebar`, so they vanish with the rail).
 *
 * Exactly ONE banner is ever shown, picked by strict priority (spec table):
 *
 *   hub unreachable > gateway_link_lost > restart_breaker > 全部账号离线
 *   (derived) > WS reconnecting > grace > account-scope criticals summary > nothing
 *
 * {@link computeFleetBannerState} is the pure decision function — exported
 * for direct unit testing of the priority/de-dup/fallback rules without
 * mounting React. {@link useFleetBannerState} wraps it as a hook (also used by
 * `Layout.tsx` directly, to drive `useDocumentTitleAlert`).
 */

export type FleetBannerKind =
  | 'hub_unreachable'
  | 'gateway_link_lost'
  | 'restart_breaker'
  | 'all_disconnected'
  | 'ws_reconnecting'
  | 'account_criticals';

export interface FleetBannerState {
  kind: FleetBannerKind;
  tone: 'red' | 'amber';
  message: string;
  /** Only set for `account_criticals` — the first account-scope critical
   *  account's id, so a click can expand the rail and scroll to its row. */
  firstCriticalAccountId: string | null;
}

/** Cap how many accounts the criticals summary spells out by name before
 *  falling back to "等 N 个账号" — keeps a storm (8-account outage, per spec
 *  01 §8) from producing an unreadably long single-line banner. */
const MAX_SUMMARY_ITEMS = 3;

interface AccountWithAttention {
  account: Account;
  attention: AccountAttention;
}

/** Accounts that carry a (non-null) attention verdict, narrowed so callers
 *  never need a non-null assertion on `.attention`. */
function accountsWithAttention(accounts: Account[]): AccountWithAttention[] {
  const result: AccountWithAttention[] = [];
  for (const account of accounts) {
    if (account.attention) {
      result.push({ account, attention: account.attention });
    }
  }
  return result;
}

/**
 * First account carrying a FLEET-scope attention matching `reason` — fleet
 * attention "arrives on EVERY account row" (T1-Fb brief), so the banner must
 * de-duplicate it into a single fleet-wide message rather than counting rows.
 */
function firstFleetAttention(
  withAttention: AccountWithAttention[],
  reason: 'restart_breaker',
): AccountWithAttention | null {
  return withAttention.find((x) => x.attention.scope === 'fleet' && x.attention.reason === reason) ?? null;
}

/** `status_since` as epoch ms; unknown / unparseable = the oldest possible write. */
function sinceMs(account: Account): number {
  const parsed = account.status_since ? Date.parse(account.status_since) : Number.NaN;
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * The first row carrying `gateway_link_lost` — but only while the link is
 * lost by the hub's own newest-write rule (`account_health.gateway_link_lost`,
 * the rule it pages on): a `gateway_link_lost` row counts only while one such
 * row is at least as new (`status_since`) as every other WhatsApp row. The
 * hub already serves a STALE link-loss stamp (an orphan row the gateway no
 * longer reports) as a plain account-scope `down`; mirroring the rule here
 * keeps a cached verdict this console never saw corrected — a missed
 * `account.status` event — from holding 「网关连接中断」 on after the live
 * accounts were re-scored.
 */
function currentGatewayLinkLoss(accounts: Account[]): AccountWithAttention | null {
  let first: AccountWithAttention | null = null;
  let newestLost = Number.NEGATIVE_INFINITY;
  let newestOther = Number.NEGATIVE_INFINITY;
  for (const account of accounts) {
    if (account.platform !== 'whatsapp') continue;
    const attention = account.attention;
    if (attention && attention.scope === 'fleet' && attention.reason === 'gateway_link_lost') {
      first ??= { account, attention };
      newestLost = Math.max(newestLost, sinceMs(account));
    } else {
      newestOther = Math.max(newestOther, sinceMs(account));
    }
  }
  return first !== null && newestLost >= newestOther ? first : null;
}

/**
 * Pure priority decision — no React, no `Date.now()` side effect (caller
 * supplies `now`), so every branch (including the de-dup and the
 * unrecognized-reason fallback baked into `attentionReasonLabel`) is testable
 * without rendering anything.
 *
 * Hidden (archived) accounts are dropped first: they are out of the pool, so
 * they raise no critical summary, count neither for the link-loss
 * newest-write rule nor for "no account connected" — the same set the hub's
 * health monitor judges.
 */
export function computeFleetBannerState(
  allAccounts: Account[],
  connectivity: Pick<ConnectivityState, 'hubUnreachable' | 'ws' | 'wsSince'>,
  now: number,
): FleetBannerState | null {
  const accounts = visibleAccounts(allAccounts);
  if (connectivity.hubUnreachable !== null) {
    return {
      kind: 'hub_unreachable',
      tone: 'red',
      message: '无法连接服务器，正在自动重试…',
      firstCriticalAccountId: null,
    };
  }

  const withAttention = accountsWithAttention(accounts);

  const gatewayLost = currentGatewayLinkLoss(accounts);
  if (gatewayLost) {
    return {
      kind: 'gateway_link_lost',
      tone: 'red',
      // No ESCALATE_TO_ADMIN_SUFFIX here (see its doc): every deploy / gateway
      // restart shows this banner for a few seconds.
      message: attentionReasonLabel('gateway_link_lost', gatewayLost.attention.since, now),
      firstCriticalAccountId: null,
    };
  }

  const breaker = firstFleetAttention(withAttention, 'restart_breaker');
  if (breaker) {
    return {
      kind: 'restart_breaker',
      tone: 'red',
      message: `${attentionReasonLabel('restart_breaker', breaker.attention.since, now)} · ${ESCALATE_TO_ADMIN_SUFFIX}`,
      firstCriticalAccountId: null,
    };
  }

  // Derived, not read off `attention` — mirrors the hub's own `channels_down`
  // definition (01 §3.3): no account `connected` while at least one is
  // actively trying (`connecting` / `qr_required`). An all-`banned`/`down`
  // fleet (nothing "live") is NOT this condition — those accounts surface
  // individually via the criticals summary below instead.
  const anyConnected = accounts.some((a) => a.status === 'connected');
  const anyLive = accounts.some((a) => a.status === 'connecting' || a.status === 'qr_required');
  if (!anyConnected && anyLive) {
    return {
      kind: 'all_disconnected',
      tone: 'red',
      message: `全部账号离线 · ${ESCALATE_TO_ADMIN_SUFFIX}`,
      firstCriticalAccountId: null,
    };
  }

  if (wsReconnectingForMs(connectivity, now) > WS_RECONNECTING_GRACE_MS) {
    return {
      kind: 'ws_reconnecting',
      tone: 'amber',
      message: '实时连接已断开，正在自动重连…',
      firstCriticalAccountId: null,
    };
  }

  const criticals = withAttention.filter(
    (x) => x.attention.level === 'critical' && x.attention.scope === 'account',
  );
  if (criticals.length > 0) {
    const shown = criticals.slice(0, MAX_SUMMARY_ITEMS);
    const parts = shown.map(
      ({ account, attention }) =>
        `${formatAccountLabel(account)} ${accountAttentionLabel(account, attention, now)}`,
    );
    const overflow = criticals.length - shown.length;
    const suffix = overflow > 0 ? `，等 ${criticals.length} 个账号` : '';
    // A ban HELD by the 403-storm guard was NOT stopped (still retrying with
    // `enabled:true`), unlike every other critical here: say so, and that an
    // admin is needed now.
    const escalate = criticals.some(({ account }) => isBanStormHeld(account))
      ? ` · ${ESCALATE_TO_ADMIN_SUFFIX}`
      : '';
    return {
      kind: 'account_criticals',
      tone: 'red',
      message: `${criticals.length} 个账号需要处理：${parts.join('；')}${suffix}${escalate}`,
      firstCriticalAccountId: criticals[0].account.id,
    };
  }

  return null;
}

/**
 * Hook form of {@link computeFleetBannerState}. The connectivity store does
 * NOT tick (T1-Fb brief) — while the socket is `reconnecting` and still
 * inside the grace window, this schedules exactly one re-render for the
 * moment the grace expires, so the amber banner appears on its own without
 * any prop/store change (a `setTimeout`, not `setInterval`: once fired, the
 * next state transition — `open` or a fresh `reconnecting` timestamp — is
 * what re-arms the effect).
 */
export function useFleetBannerState(): FleetBannerState | null {
  const accountsQuery = useAccounts();
  const connectivity = useConnectivityStore();
  const [, forceRerender] = useState(0);

  useEffect(() => {
    if (connectivity.ws !== 'reconnecting' || connectivity.wsSince === null) return;
    const remaining = WS_RECONNECTING_GRACE_MS - wsReconnectingForMs(connectivity, Date.now());
    if (remaining <= 0) return; // already past grace — no timer needed
    // +50ms: land just after the boundary, never a hair before it (avoids a
    // second timer from an off-by-one re-check).
    const timer = window.setTimeout(() => forceRerender((t) => t + 1), remaining + 50);
    return () => window.clearTimeout(timer);
    // Deliberately just the two primitives the effect reads, not the whole
    // `connectivity` snapshot: `markHubUnreachable`/`markHubReachable`
    // changing `hubUnreachable` must NOT re-arm this timer (same rationale as
    // `ThreadPanel.tsx`'s chip resolvers — depend on the stable primitives a
    // callback actually reads, not the whole object it was destructured from).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectivity.ws, connectivity.wsSince]);

  return computeFleetBannerState(accountsQuery.data ?? [], connectivity, Date.now());
}

const TONE_CLASSES: Record<FleetBannerState['tone'], string> = {
  red: 'bg-red-600 text-white hover:bg-red-600/90 dark:bg-red-700 dark:hover:bg-red-700/90',
  amber:
    'bg-amber-500 text-amber-950 hover:bg-amber-500/90 dark:bg-amber-600 dark:text-amber-50 dark:hover:bg-amber-600/90',
};

export interface FleetStatusBannerProps {
  /** Fired only for the clickable variant (`account_criticals`) — spec:
   *  "Clicking the summary expands the rail and scrolls to the first
   *  critical row." `Layout.tsx` owns the rail + the row lookup; this
   *  component only reports WHICH account. */
  onCriticalClick?: (accountId: string) => void;
}

/**
 * Renders {@link useFleetBannerState}'s current pick, or nothing. The only
 * clickable variant is `account_criticals` (a `<button>`, matching the
 * existing `QrAttentionBanner` / `BanAttentionBanner` affordance); every
 * other variant is a plain alert bar — there's no single row to jump to for
 * "the whole gateway link is down".
 */
export function FleetStatusBanner({
  onCriticalClick,
}: FleetStatusBannerProps): React.JSX.Element | null {
  const state = useFleetBannerState();
  if (!state) return null;

  const Icon = state.kind === 'ws_reconnecting' ? Loader2 : AlertTriangle;
  const content = (
    <>
      <Icon
        className={cn('size-4 shrink-0', state.kind === 'ws_reconnecting' && 'animate-spin')}
        aria-hidden="true"
      />
      <span className="truncate">{state.message}</span>
    </>
  );
  const sharedClassName = cn(
    'flex w-full shrink-0 items-center gap-2 px-4 py-2 text-left text-[13px] font-bold shadow-neu-raised',
    TONE_CLASSES[state.tone],
  );

  if (state.kind === 'account_criticals' && state.firstCriticalAccountId) {
    const accountId = state.firstCriticalAccountId;
    return (
      <button
        type="button"
        role="alert"
        data-testid="fleet-status-banner"
        data-banner-kind={state.kind}
        onClick={() => onCriticalClick?.(accountId)}
        className={cn(sharedClassName, 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white')}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      role="alert"
      data-testid="fleet-status-banner"
      data-banner-kind={state.kind}
      className={sharedClassName}
    >
      {content}
    </div>
  );
}
